import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../services/api';
import AdminHeader from '../../components/AdminHeader';
import AudioPlayer from '../../components/AudioPlayer';
import './admin.css';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: '1rem', padding: '0.55rem 0', borderBottom: '1px solid #e5e7eb' }}>
      <span style={{ width: 160, flexShrink: 0, color: '#6b7280', fontSize: '0.85rem', fontWeight: 500 }}>
        {label}
      </span>
      <span style={{ fontSize: '0.9rem', color: '#111827' }}>{value || '—'}</span>
    </div>
  );
}

export default function AdminSongReview() {
  const { songId } = useParams();
  const [song, setSong]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  // Placement override state
  const [placementCats, setPlacementCats]       = useState([]);
  const [overrideCatId, setOverrideCatId]       = useState('');
  const [overrideSaving, setOverrideSaving]     = useState(false);
  const [overrideSaved, setOverrideSaved]       = useState(false);
  const [overrideError, setOverrideError]       = useState('');

  useEffect(() => {
    api.get(`/api/admin/songs/${songId}/review`)
      .then((res) => {
        const s = res.data?.song;
        if (!s) {
          setError('No song data returned. The server may need to be restarted to pick up new routes.');
        } else {
          setSong(s);
          if (s.admin_placement_category_id) {
            setOverrideCatId(String(s.admin_placement_category_id));
          }
        }
      })
      .catch((err) => setError(err.response?.data?.error || 'Failed to load song.'))
      .finally(() => setLoading(false));

    api.get('/api/admin/placement-categories')
      .then((res) => setPlacementCats(res.data.categories || []))
      .catch(() => {});
  }, [songId]);

  async function handleOverrideSave() {
    if (!overrideCatId) {
      setOverrideError('Please select a placement.');
      return;
    }
    setOverrideSaving(true);
    setOverrideError('');
    setOverrideSaved(false);
    try {
      const res = await api.post(`/api/admin/songs/${songId}/override-placement`, {
        placement_category_id: Number(overrideCatId),
      });
      setSong((prev) => ({ ...prev, ...res.data.song }));
      setOverrideSaved(true);
      setTimeout(() => setOverrideSaved(false), 4000);
    } catch (err) {
      setOverrideError(err.response?.data?.error || 'Failed to save override.');
    } finally {
      setOverrideSaving(false);
    }
  }

  const backBtn = (
    <Link
      to="/admin/dashboard"
      style={{
        display:         'inline-block',
        background:      '#0f1e3d',
        color:           '#fff',
        padding:         '0.5rem 1.1rem',
        borderRadius:    '6px',
        textDecoration:  'none',
        fontSize:        '0.875rem',
        fontWeight:      500,
      }}
    >
      ← Back to Judges &amp; Assignments
    </Link>
  );

  return (
    <div className="c-page">
      <AdminHeader />
      <main className="c-main" style={{ maxWidth: 760, margin: '0 auto', padding: '2rem 1rem' }}>
        {backBtn}

        {loading && (
          <div className="c-loading" style={{ marginTop: '3rem' }}>
            <div className="c-spinner" aria-label="Loading…" />
          </div>
        )}

        {error && (
          <div className="c-alert" style={{ marginTop: '1.5rem' }}>{error}</div>
        )}

        {song && (
          <>
            <h1 style={{ margin: '1.5rem 0 0.25rem', fontSize: '1.6rem', color: '#0f1e3d' }}>
              {song.title}
            </h1>
            <p style={{ margin: '0 0 1.5rem', color: '#6b7280', fontSize: '0.95rem' }}>
              {song.category_name}
            </p>

            {/* ── Conflict banner ── */}
            {song.evaluation_status === 'conflict' && (
              <div style={{
                background:   '#fffbeb',
                border:       '1px solid #fde68a',
                borderLeft:   '4px solid #f59e0b',
                borderRadius: '8px',
                padding:      '1rem 1.25rem',
                marginBottom: '1.5rem',
              }}>
                <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '0.4rem', fontSize: '0.95rem' }}>
                  ⚠ Placement conflict detected
                </div>
                <div style={{ color: '#78350f', fontSize: '0.875rem', lineHeight: 1.5 }}>
                  Judges chose different placements for this song. They have been notified by email and their evaluations reset for resubmission.
                  You may set the final placement below if the conflict cannot be resolved.
                </div>
              </div>
            )}

            {/* ── Admin placement override ── */}
            {(song.evaluation_status === 'conflict' || song.admin_placement_category_id) && (
              <div className="c-form-card" style={{ marginBottom: '1.5rem' }}>
                <h2 className="c-form-card__title" style={{ marginBottom: '0.75rem' }}>
                  Set Final Placement
                </h2>

                {song.admin_placement_category_id && (
                  <div style={{
                    marginBottom: '0.75rem',
                    fontSize:     '0.875rem',
                    color:        '#374151',
                  }}>
                    Current override: <strong>{song.admin_placement_name || '—'}</strong>
                  </div>
                )}

                {overrideError && (
                  <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{overrideError}</div>
                )}
                {overrideSaved && (
                  <div style={{
                    background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px',
                    padding: '0.5rem 0.75rem', color: '#14532d', fontSize: '0.875rem',
                    marginBottom: '0.75rem',
                  }}>
                    Placement override saved.
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <select
                    className="c-input"
                    style={{ flex: 1 }}
                    value={overrideCatId}
                    onChange={(e) => setOverrideCatId(e.target.value)}
                    disabled={overrideSaving}
                  >
                    <option value="">— Select placement —</option>
                    {placementCats.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <button
                    className="c-btn"
                    onClick={handleOverrideSave}
                    disabled={overrideSaving || !overrideCatId}
                  >
                    {overrideSaving ? 'Saving…' : 'Save Override'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Contestant info card ── */}
            <div className="c-form-card" style={{ marginBottom: '1.5rem' }}>
              <h2 className="c-form-card__title" style={{ marginBottom: '0.25rem' }}>
                Contestant
              </h2>
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '0.5rem' }}>
                <InfoRow label="Full name"        value={song.full_name} />
                <InfoRow label="Email"            value={song.email} />
                <InfoRow label="Phone"            value={song.phone} />
                <InfoRow label="Membership"       value={song.is_member ? 'Member' : 'Non-member'} />
                <InfoRow label="Submission date"  value={formatDate(song.created_at)} />
                <InfoRow
                  label="Payment"
                  value={
                    song.payment_status === 'paid'   ? 'Paid' :
                    song.payment_status === 'waived' ? 'Waived' :
                                                       'Pending'
                  }
                />
              </div>
            </div>

            {/* ── Song content panel ── */}
            <div className="c-form-card">
              <h2 className="c-form-card__title" style={{ marginBottom: '0.75rem' }}>
                Song Content
              </h2>

              {song.audio_url && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <AudioPlayer src={song.audio_url} />
                </div>
              )}

              {song.lyrics_text && (
                <div>
                  <p style={{
                    margin: '0 0 0.5rem',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: '#374151',
                  }}>
                    Lyrics
                  </p>
                  <pre style={{
                    whiteSpace:   'pre-wrap',
                    wordBreak:    'break-word',
                    fontFamily:   'inherit',
                    fontSize:     '0.875rem',
                    lineHeight:   1.7,
                    color:        '#1f2937',
                    background:   '#f9fafb',
                    border:       '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding:      '1rem',
                    maxHeight:    '400px',
                    overflowY:    'auto',
                    margin:       0,
                  }}>
                    {song.lyrics_text}
                  </pre>
                </div>
              )}

              {!song.audio_url && !song.lyrics_text && (
                <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                  No audio or lyrics on file for this song.
                </p>
              )}
            </div>

            <div style={{ marginTop: '2rem' }}>
              {backBtn}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
